import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Plus, RefreshCw, Mail, Calendar, Users, TrendingUp, Camera, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import { EmptyPlaceholder } from '@/components/EmptyPlaceholder';
import { useAuth } from '@/context/auth';
import { hasSuperAdminAccess, hasOrgAdminAccess, hasRestAdminAccess } from '@/context/auth/types';
import EmailService, { EmailCampaign } from '@/api/email';
import { Badge } from '@/components/ui/badge';
import CampaignForm from '@/components/email/forms/CampaignForm';
import { EmailCampaignList } from '@/components/email/campaigns';
import { capturePreviewDialogScreenshot, waitForPreviewDialog } from '@/utils/capturePreviewDialog';
import { processTemplatePreview } from '@/utils/processTemplateImage';
import { uploadCampaignPreview, deleteOldCampaignPreviews } from '@/services/campaignPreviewService';
import PreviewDialog from '@/components/email/preview/PreviewDialog';

interface CampaignsTabProps {
  onError?: (error: Error) => void;
}

type ViewMode = 'list' | 'create' | 'edit';

const CampaignsTab: React.FC<CampaignsTabProps> = ({ onError }) => {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState<ViewMode>('list');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Preview capture state
  const [capturingPreview, setCapturingPreview] = useState(false);
  const [captureStatus, setCaptureStatus] = useState<string>('');
  const [campaignForCapture, setCampaignForCapture] = useState<EmailCampaign | null>(null);

  // Preview dialog state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [resolvedPreview, setResolvedPreview] = useState<{
    html: string;
    subject: string;
    resolved_variables: Record<string, Record<string, string>>;
    customer: { id: string; email: string; first_name: string; last_name: string };
  } | null>(null);

  // Customer preview state
  const [recentCustomers, setRecentCustomers] = useState<Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  }>>([]);

  const fetchCampaigns = async () => {
    if (!user?.organisationId) {
      const errorMessage = 'No organisation access available';
      setError(errorMessage);
      setLoading(false);
      if (onError) onError(new Error(errorMessage));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const fetchedCampaigns = await EmailService.fetchCampaigns(user.organisationId);
      setCampaigns(fetchedCampaigns);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      const errorMessage = 'Failed to load campaigns. Please try again.';
      setError(errorMessage);
      if (onError && err instanceof Error) onError(err);

      toast({
        title: 'Error loading campaigns',
        description: 'There was a problem fetching your campaigns.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [user]);

  // Fetch recent customers for preview
  useEffect(() => {
    const fetchRecentCustomers = async () => {
      if (!user?.organisationId) return;

      try {
        console.log('[CampaignsTab] Fetching recent customers for preview');
        const customers = await EmailService.getRecentCustomers(user.organisationId, 50);
        setRecentCustomers(customers);
      } catch (error) {
        console.error('[CampaignsTab] Error fetching customers:', error);
      }
    };

    fetchRecentCustomers();
  }, [user?.organisationId]);

  // Check if user can create campaigns
  const canCreateCampaign = user && (
    hasSuperAdminAccess(user.role) ||
    hasOrgAdminAccess(user.role) ||
    hasRestAdminAccess(user.role)
  );

  // View transition handlers
  const handleCreateCampaign = () => {
    setCurrentView('create');
    setSelectedCampaignId(null);
  };

  const handleEditCampaign = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setCurrentView('edit');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedCampaignId(null);
    fetchCampaigns(); // Refresh list
  };

  const handleCampaignSaved = () => {
    setCurrentView('list');
    fetchCampaigns(); // Refresh list with new campaign
  };

  // Preview capture handler
  const handleCapturePreview = async (campaign: EmailCampaign) => {
    if (!user?.organisationId) {
      toast({
        title: 'Cannot capture preview',
        description: 'Organisation not found',
        variant: 'destructive'
      });
      return;
    }

    // Check if we have customers for real data
    if (recentCustomers.length === 0) {
      toast({
        title: 'No Customers Available',
        description: 'Add customers before capturing campaign previews.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setCapturingPreview(true);
      setCampaignForCapture(campaign);
      setCaptureStatus('Resolving variables with customer data...');

      console.log('[CampaignsTab] Starting preview capture for:', campaign.campaign_name);

      // Step 1: Use first customer for preview
      const previewCustomer = recentCustomers[0];
      console.log('[CampaignsTab] Using customer for preview:', previewCustomer.email);

      // Step 2: Get real preview with resolved variables from backend
      const preview = await EmailService.previewWithVariables(
        campaign.id,
        previewCustomer.id,
        user.organisationId
      );

      // Step 3: Open preview dialog with real data
      setCaptureStatus('Opening preview...');
      setResolvedPreview(preview);
      setShowPreviewDialog(true);

      // Step 4: Wait for dialog to open and render
      setCaptureStatus('Waiting for preview to render...');
      await waitForPreviewDialog(10000);

      // Step 5: Additional wait for content to fully load
      setCaptureStatus('Loading preview content...');
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Step 6: Capture screenshot
      setCaptureStatus('Capturing screenshot...');
      console.log('[CampaignsTab] Capturing screenshot...');
      const screenshot = await capturePreviewDialogScreenshot(500);

      // Step 7: Close preview dialog
      setShowPreviewDialog(false);

      // Step 8: Process image
      setCaptureStatus('Processing image...');
      console.log('[CampaignsTab] Processing image...');
      const processed = await processTemplatePreview(screenshot);

      // Step 9: Upload to storage
      setCaptureStatus('Uploading to storage...');
      console.log('[CampaignsTab] Uploading to storage...');
      const publicUrl = await uploadCampaignPreview(campaign.id, processed);

      // Step 10: Update campaign record
      setCaptureStatus('Updating campaign...');
      console.log('[CampaignsTab] Updating campaign record...');
      await EmailService.updateCampaign(campaign.id, {
        preview_image_url: publicUrl
      });

      // Step 11: Refresh campaigns list
      await fetchCampaigns();

      // Step 12: Cleanup old previews (non-blocking)
      deleteOldCampaignPreviews(campaign.id).catch(err => {
        console.warn('[CampaignsTab] Cleanup failed (non-critical):', err);
      });

      toast({
        title: 'Preview captured!',
        description: `Preview image saved for "${campaign.campaign_name}"`,
      });

      console.log('[CampaignsTab] Preview capture complete');

    } catch (error) {
      console.error('[CampaignsTab] Preview capture failed:', error);

      // Close preview dialog if it's still open
      setShowPreviewDialog(false);

      toast({
        title: 'Capture failed',
        description: error instanceof Error ? error.message : 'Failed to capture preview',
        variant: 'destructive'
      });
    } finally {
      setCapturingPreview(false);
      setCaptureStatus('');
      setCampaignForCapture(null);
    }
  };

  // Preview campaign handler
  const handlePreviewCampaign = async (campaign: EmailCampaign) => {
    if (recentCustomers.length === 0) {
      toast({
        title: 'No Customers Available',
        description: 'Add customers before previewing campaigns.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const previewCustomer = recentCustomers[0];
      const preview = await EmailService.previewWithVariables(
        campaign.id,
        previewCustomer.id,
        user?.organisationId || ''
      );

      setResolvedPreview(preview);
      setShowPreviewDialog(true);
    } catch (error: any) {
      console.error('Error generating preview:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate preview',
        variant: 'destructive'
      });
    }
  };

  // Delete campaign handler
  const handleDeleteCampaign = async (campaign: EmailCampaign) => {
    if (campaign.status !== 'draft') {
      toast({
        title: 'Cannot delete campaign',
        description: 'Only draft campaigns can be deleted.',
        variant: 'destructive'
      });
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete "${campaign.campaign_name}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await EmailService.deleteCampaign(campaign.id);

      toast({
        title: 'Campaign deleted',
        description: 'The campaign has been successfully deleted.',
      });

      fetchCampaigns();
    } catch (error: any) {
      console.error('Error deleting campaign:', error);
      toast({
        title: 'Error deleting campaign',
        description: error.message || 'There was a problem deleting the campaign.',
        variant: 'destructive'
      });
    }
  };

  // Campaign ideas
  const campaignIdeas = [
    {
      title: 'Welcome Newsletter',
      description: 'Send a welcome email to new subscribers with your story and best offers.',
      scenario: 'Segment: New subscribers in the last 7 days'
    },
    {
      title: 'Monthly Specials',
      description: 'Share this month\'s special menu items and promotions.',
      scenario: 'Segment: All active email subscribers'
    },
    {
      title: 'Re-engagement Campaign',
      description: 'Win back customers who haven\'t visited in a while with exclusive offers.',
      scenario: 'Segment: Customers inactive for 60+ days'
    }
  ];

  // Render form views (create/edit)
  if (currentView === 'create' || currentView === 'edit') {
    return (
      <div className="py-4 overflow-hidden">
        <CampaignForm
          campaignId={selectedCampaignId || undefined}
          onSave={handleCampaignSaved}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Empty state - no campaigns
  if (!loading && !error && campaigns.length === 0) {
    return (
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Email Campaigns</h3>
          {canCreateCampaign && (
            <Button onClick={handleCreateCampaign} className="gap-1">
              <Plus className="h-4 w-4" />
              Create Campaign
            </Button>
          )}
        </div>

        <EmptyPlaceholder
          title="No email campaigns yet"
          description="Create your first email campaign to start engaging with your customers via email."
          icon={<AlertCircle className="h-12 w-12" />}
        >
          {canCreateCampaign && (
            <Button onClick={handleCreateCampaign}>
              New Email Campaign
            </Button>
          )}
        </EmptyPlaceholder>

        <div className="mt-8">
          <h3 className="text-lg font-medium mb-4">Campaign Ideas</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {campaignIdeas.map((idea, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-base">{idea.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{idea.description}</CardDescription>
                </CardContent>
                <CardFooter>
                  <p className="text-xs text-muted-foreground">{idea.scenario}</p>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Email Campaigns</h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchCampaigns}
            disabled={loading}
            size="icon"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>

          {canCreateCampaign && (
            <Button onClick={handleCreateCampaign} className="gap-1">
              <Plus className="h-4 w-4" />
              Create Campaign
            </Button>
          )}
        </div>
      </div>

      <Separator />

      {/* Campaign List */}
      {error ? (
        <Card>
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center text-center">
              <AlertCircle className="h-8 w-8 text-destructive mb-2" />
              <p className="text-sm text-muted-foreground">{error}</p>
              <Button variant="outline" onClick={fetchCampaigns} className="mt-4">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <EmailCampaignList
          campaigns={campaigns}
          loading={loading}
          onEdit={(campaign) => handleEditCampaign(campaign.id)}
          onDelete={handleDeleteCampaign}
          onCapturePreview={handleCapturePreview}
          onPreview={handlePreviewCampaign}
          onRefresh={fetchCampaigns}
          userRole={user?.role}
        />
      )}

      {/* Preview Capture Loading Overlay */}
      {capturingPreview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-2xl max-w-md w-full mx-4">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <Camera className="h-8 w-8 text-primary" />
                <Loader2 className="h-4 w-4 animate-spin absolute -top-1 -right-1 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">Capturing Preview</h3>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {captureStatus || 'Processing...'}
                </p>
              </div>

              {campaignForCapture && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Campaign:</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {campaignForCapture.campaign_name}
                  </p>
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                This will take a few seconds. Please wait...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      {resolvedPreview && (
        <PreviewDialog
          open={showPreviewDialog}
          onOpenChange={setShowPreviewDialog}
          resolvedPreview={resolvedPreview}
        />
      )}
    </div>
  );
};

export default CampaignsTab;
